import { QueryResult, Predicate, Query } from 'olympe';
import { fetchAhpValue, getEvaluationValueOfInspection } from './observationData';
import { addAlpha } from '../utils';
import System from "../../../../models/System";
import Observation from "../../../../models/Observation";
import Unit from "../../../../models/Unit";
import Component from "../../../../models/Component";
import Aspect from "../../../../models/Aspect";
import Report from "../../../../models/Report";
import InspectionResultMapping from "../../../../models/InspectionResultMapping";
import InspectionEntity from "../../../../models/InspectionEntity";

/**
 * @param {Object} data
 * @return {number}
 */
export const getChartHeight = (data) => {
    const minHeight = 350;
    const totalBars = data.labels.length;
    // Make sure that we take the label size into account (take the max number of lines by label)
    const labelLines = data.labels.reduce((acc, label) => Math.max(acc, label.length), 1);
    return totalBars > 3 ? (totalBars - 3) * (20 + 12 * labelLines) + minHeight : minHeight;
}

/**
 * @param {System} system
 * @param {Unit} unit
 * @param {Report?} report
 * @return {[{component:Component, inspections:QueryResult<Inspection, Observation>}[], QueryResult<Aspect>, Map<number, number>]}
 */
export const getSystemDefectChartRawData = (system, unit, report = undefined) => {

    // Create includeRelation and includePredicate
    // -- Use the report object, if defined
    const includeFollowRel = report !== undefined ? Report.observationsRel.getInverse() : Unit.observationsRel.getInverse();
    const includePredicate = report !== undefined ? Predicate.in(report) : Predicate.in(unit);

    // Get component of the system that have an observation
    const componentsWithObservations = system?.follow(System.allComponentsRel)
        .andReturn()
        .follow(Observation.targetRel.getInverse())
        .follow(includeFollowRel)
        .filter(includePredicate)
        .executeFromCache() ?? QueryResult.empty();

    // Add inspections having an observation to the components list
    const componentsAndInspectionsWithObservations = componentsWithObservations.map((component) => {
        return {
            component,
            inspections: component
                .follow(Component.inspectionsRel)
                .andReturn()
                .follow(Observation.targetRel.getInverse())
                .andReturn()
                .follow(includeFollowRel)
                .filter(includePredicate)
                .executeFromCache(),
        };
    }).filter((component) => component.inspections.size() > 0);

    const aspects = Query.instancesOf(Aspect).sortBy(Aspect.nameProp).executeFromCache();
    const inspectionResultMapping = Query.instancesOf(InspectionResultMapping).executeFromCache().reduce((map, item) => {
        return map.set(item.getInspectionMark(), item.getResult());
    }, new Map());

    // Sort the components by result value.
    const sortedEntities = componentsAndInspectionsWithObservations.map((componentWithInspections) => {
        const value = getComponentResultsOverallAspect(componentWithInspections, aspects, inspectionResultMapping);
        return { componentWithInspections, value };
    }).sort((a, b) => b.value - a.value).map((item) => item.componentWithInspections);

    return [sortedEntities, aspects, inspectionResultMapping];
}

/**
 * @param {string} graphResult
 * @param {Array<{component: Component, inspections:QueryResult<Inspection, Observation>}>} componentsWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {{datasets, labels}}
 */
export const buildSystemChartDatasets = (graphResult, componentsWithInspections, aspects, mapping) => {
    switch (graphResult) {
        case 'Overall components condition':
            return renderChartOverall(componentsWithInspections, aspects, mapping, false);
        case 'Overall components condition weighted':
            return renderChartOverall(componentsWithInspections, aspects, mapping, true);
        case 'Component conditions':
            return renderChartOnAspects(componentsWithInspections, aspects, mapping, false);
        case 'Component conditions weighted':
            return renderChartOnAspects(componentsWithInspections, aspects, mapping, true);
        default:
            return renderChartCompleteness(componentsWithInspections);
    }
};

/**
 * @param {!QueryResult<Inspection, Observation>} inspections
 * @param {!QueryResult} aspects
 * @param {Map<number, number>} mapping
 * @param {boolean} showResults
 * @return {*}
 */
const sortInspections = (inspections, aspects, mapping, showResults) => {
    return inspections
        .map((inspection) => {
            const value = showResults
                ? getInspectionResult(inspection, mapping)
                : getInspectionResultOverallAspects(inspection, aspects, mapping);
            return {
                inspection,
                value,
            };
        })
        .sort((a, b) => b.value - a.value)
        .map((item) => item.inspection);
};

/**
 * @param {Component} component
 * @param {Unit} unit
 * @param {Report?} report
 * @return {[QueryResult<Inspection, Observation>, QueryResult<Aspect>, Map<number, number>]}
 */
export const getComponentDefectChartRawData = (component, unit, report = undefined) => {

    // Create includeRelation and includePredicate
    // -- Use the report object, if defined
    const includeFollowRel = report !== undefined ? Report.observationsRel.getInverse() : Unit.observationsRel.getInverse();
    const includePredicate = report !== undefined ? Predicate.in(report) : Predicate.in(unit);

    const inspectionsWithObservation = component?.follow(Component.inspectionsRel)
        .sortBy(InspectionEntity.nameProp)
        .andReturn()
        .follow(Observation.targetRel.getInverse())
        .andReturn()
        .follow(includeFollowRel)
        .filter(includePredicate)
        .executeFromCache() ?? QueryResult.empty();

    const aspects = Query.instancesOf(Aspect).executeFromCache();
    const inspectionResultMapping = Query.instancesOf(InspectionResultMapping).executeFromCache().reduce((map, item) => {
        return map.set(item.getInspectionMark(), item.getResult());
    }, new Map());

    return [inspectionsWithObservation, aspects, inspectionResultMapping];
};

/**
 * @param {string} graphResult
 * @param {!QueryResult<Inspection, Observation>} inspectionsWithObservation
 * @param {!QueryResult} aspects
 * @param {Map<number, number>} mapping
 * @return {{datasets, labels}}
 */
export const buildComponentChartDatasets = (graphResult, inspectionsWithObservation, aspects, mapping) => {
    switch (graphResult) {
        case 'Inspection results':
            return renderComponentChartOverall(
                sortInspections(inspectionsWithObservation, aspects, mapping, true),
                aspects,
                mapping,
                true
            );
        case 'Inspection results on aspects':
            return renderComponentChartOnAspects(
                sortInspections(inspectionsWithObservation, aspects, mapping, false),
                aspects,
                mapping
            );
        default:
            return renderComponentChartOverall(
                sortInspections(inspectionsWithObservation, aspects, mapping, false),
                aspects,
                mapping,
                false
            );
    }
};

/**
 * Render the component's defect chart Inspection results on aspects
 * @param {QueryResult<Inspection, Observation>} inspectionsWithObservation
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {{datasets, labels}}
 */
const renderComponentChartOnAspects = (inspectionsWithObservation, aspects, mapping) => {
    // -- Init the datasets array
    const datasets = [];
    // -- Init the labels array
    const labels = [];

    for (const aspect of aspects.values()) {
        const aspectColor = aspect.getColor().toHexString();
        const aspectName = aspect.getName();
        // -- Init the data array
        const data = [];
        const res = {
            label: aspectName,
            data: data,
            borderColor: aspectColor,
            backgroundColor: addAlpha(aspectColor, 0.5),
        };
        for (const inspection of inspectionsWithObservation.values()) {
            const values = getInspectionResultForAspect(inspection, aspect, mapping);

            labels.push(inspection[0].getName());
            data.push(values);
        }
        // -- Stored datasets
        datasets.push(res);
    }

    return {
        labels: splitLabels([...new Set(labels)], 25),
        datasets,
    };
};

/**
 * Render the component's defect chart Inspection results
 * @param {QueryResult<Inspection, Observation>} inspectionsWithObservation
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @param {Boolean} showResults
 * @return {{datasets, labels}}
 */
const renderComponentChartOverall = (inspectionsWithObservation, aspects, mapping, showResults) => {
    const result = inspectionsWithObservation.map((inspection) => {
        const value = showResults
            ? getInspectionResult(inspection, mapping)
            : getInspectionResultOverallAspects(inspection, aspects, mapping);
        return {
            title: inspection[0].getName(),
            value,
        };
    });

    const labels = result.map((item) => item.title);

    return {
        labels: splitLabels(labels, 25),
        datasets: [
            {
                label: 'Overall',
                data: result.map((item) => item.value),
                borderColor: 'rgb(0, 109, 176)',
                backgroundColor: 'rgba(0, 109, 176, 0.5)',
                borderWidth: 1,
            },
        ],
    };
};

/**
 * @param {[Inspection, Observation]} inspection
 * @param {!QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getInspectionResultOverallAspects = (inspection, aspects, mapping) => {
    // Init return value as plain inspection result
    let result = getInspectionResult(inspection, mapping);

    // Init overall weight
    let weight = 0;
    let length = 0;

    for (const aspect of aspects.values()) {
        const fetchedAhpValues = fetchAhpValue(aspect, inspection[0]);
        // Update overall weight
        weight += fetchedAhpValues;
        length += 1;
    }

    // Final weight
    weight = length > 0 ? weight / length : weight;

    // Set final result
    return result * weight;
};

/**
 * Render the system's defect chart overall aspects
 * @param {Array<{component: Component, inspections:QueryResult<Inspection, Observation>}>} componentsWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @param {boolean} isWeighted
 * @return {{datasets, labels}}
 */
const renderChartOverall = (componentsWithInspections, aspects, mapping, isWeighted) => {
    const result = componentsWithInspections.map((componentWithInspections) => {
        const value = isWeighted
            ? getComponentResultsOverallAspectsWeighted(componentWithInspections, aspects, mapping)
            : getComponentResultsOverallAspect(componentWithInspections, aspects, mapping);
        return {
            title: componentWithInspections.component.getName(),
            value: value,
        };
    });

    const labels = result.map((item) => item.title);
    return {
        labels: splitLabels(labels, 25),
        datasets: [
            {
                label: 'Overall',
                data: result.map((item) => item.value),
                borderColor: 'rgb(0, 109, 176)',
                backgroundColor: 'rgba(0, 109, 176, 0.5)',
                borderWidth: 1,
            },
        ],
    };
};

/**
 * @param {{component: Component, inspections:QueryResult<Inspection, Observation>}} componentWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getComponentResultsOverallAspectsWeighted = (componentWithInspections, aspects, mapping) => {
    // Loop overall aspects and return the average of all aspects
    return aspects.reduce((sum, aspect) => {
        return sum + getComponentResultsByAspectWeighted(componentWithInspections, aspect, mapping)
    }, 0) / aspects.size();
};

/**
 * @param {{component: Component, inspections:QueryResult<Inspection, Observation>}} componentWithInspections
 * @param {Aspect} aspects
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getComponentResultsByAspectWeighted = (componentWithInspections, aspects, mapping) => {
    const result = getComponentResultsByAspect(componentWithInspections, aspects, mapping);
    // Get AHP Value
    const fetchedAhpValue = fetchAhpValue(aspects, componentWithInspections.component);
    return result * fetchedAhpValue;
};

/**
 * @param {{component: Component, inspections:QueryResult<Inspection, Observation>}} componentWithInspections
 * @param {Aspect} aspect
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getComponentResultsByAspect = (componentWithInspections, aspect, mapping) => {
    // Sum up all inspections results
    return componentWithInspections.inspections.reduce((sum, inspection) => {
        return sum + getInspectionResultForAspect(inspection, aspect, mapping);
    }, 0);
};

/**
 * @param {[Inspection, Observation]} inspection
 * @param {Aspect} aspect
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getInspectionResultForAspect = (inspection, aspect, mapping) => {
    // Init return value as plain inspection result
    let result = getInspectionResult(inspection, mapping);
    // Fetch any ahp value attached to the inspection for the given aspect
    const fetchedAhpValue = fetchAhpValue(aspect, inspection[0]);

    // Add weight to inspection result
    result = result * fetchedAhpValue;
    // Update map and return
    return result;
};

/**
 * @param {[Inspection, Observation]} inspection
 * @param {Map<number, number>} mapping
 * @return {number}
 */
const getInspectionResult = (inspection, mapping) => {
    const evaluationValue = getEvaluationValueOfInspection(inspection[1]);
    return mapping.get(evaluationValue) ?? 0;
};

/**
 * @param {{component: Component, inspections:QueryResult<Inspection, Observation>}} componentWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {number}
 */
export const getComponentResultsOverallAspect = (componentWithInspections, aspects, mapping) => {
    // Save all components results by aspect and return the average;
    return aspects.reduce((sum, aspect) => {
        return sum + getComponentResultsByAspect(componentWithInspections, aspect, mapping);
    }, 0) / aspects.size();
};

/**
 * Render the system's defect chart Component conditions
 * @param {Array<{component: Component, inspections:QueryResult<Inspection, Observation>}>} componentsWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @param {boolean} isWeighted
 * @return {{labels, datasets}}
 */
const renderChartOnAspects = (componentsWithInspections, aspects, mapping, isWeighted) => {
    // -- Init the datasets array
    const datasets = [];
    // -- Init the labels array
    const labels = [];

    for (const aspect of aspects.values()) {
        const aspectColor = aspect.getColor().toHexString();
        const aspectName = aspect.getName();
        // -- Init the data array
        const data = [];
        const res = {
            label: aspectName,
            data: data,
            borderColor: aspectColor,
            backgroundColor: addAlpha(aspectColor, 0.5),
        };
        for (const componentWithInspections of componentsWithInspections) {
            const values = isWeighted
                ? getComponentResultsByAspectWeighted(componentWithInspections, aspect, mapping)
                : getComponentResultsByAspect(componentWithInspections, aspect, mapping);
            labels.push(componentWithInspections.component.getName());
            data.push(values);
        }
        // -- Stored datasets
        datasets.push(res);
    }
    return {
        labels: splitLabels([...new Set(labels)], 25),
        datasets,
    };
};

/**
 * Render the system's defect chart Completeness
 * @param {Array<{component: Component, inspections:QueryResult<Inspection, Observation>}>} componentsWithInspections
 * @return {{labels, datasets}}
 */
export const renderChartCompleteness = (componentsWithInspections) => {
    const result = componentsWithInspections.map((componentWithInspections) => {
        const inspectionsWithEvaluation = componentWithInspections.inspections
            .map((inspection) => getEvaluationValueOfInspection(inspection[1]))
            .filter((inspection) => inspection !== null);
        const completenessValue =
            (inspectionsWithEvaluation.length / componentWithInspections.inspections.size()) * 100;

        return {
            title: componentWithInspections.component.getName(),
            value: completenessValue,
        };
    });

    const labels = result.map((item) => item.title);
    return {
        labels: splitLabels(labels, 25),
        datasets: [
            {
                label: 'Completeness',
                data: result.map((item) => item.value),
                borderColor: 'rgb(0, 109, 176)',
                backgroundColor: 'rgba(0, 109, 176, 0.5)',
            },
        ],
    };
};

/**
 * @param {{component: Component, inspections:QueryResult<Inspection, Observation>}} componentWithInspections
 * @param {QueryResult<Aspect>} aspects
 * @param {Map<number, number>} mapping
 * @return {{name: string, defectValue: number}[]}
 */
export const formPinsArray = (componentWithInspections, aspects, mapping) => {
    return aspects.map((aspect) => {
        return {
            name: aspect.getName(),
            defectValue: getComponentResultsByAspect(componentWithInspections, aspect, mapping),
        };
    });
};

/**
 * @param {string[]} labels
 * @param {number} maxLabelLength
 * @return {string[][]}
 */
const splitLabels = (labels, maxLabelLength) => {
    return labels.map((label) => {
        return label.split(/\s+/g).reduce((result, word) => {
            const cursor = result.length - 1;
            if (cursor < 0 || result[cursor].length + word.length + 1 > maxLabelLength) {
                result.push(word);
            } else {
                result[cursor] += ` ${word}`;
            }
            return result;
        }, []);
    });
};
